import {
    ATTRIBUTE_CLIENT_ID,
    ATTRIBUTE_CLIENT_SCOPES,
    ATTRIBUTE_DEPLOY_HUB_CODE,
    ATTRIBUTE_DEPLOY_HUB_ID,
    ATTRIBUTE_USER_COUNTRY,
    ATTRIBUTE_USER_ID,
    ATTRIBUTE_USER_ORG_ID,
    ATTRIBUTE_USER_ORG_ROLE,
    ATTRIBUTE_USER_ULT_SITE,
} from '@local/metrics/metrics.constants';
import { decodeToken, isExpired } from 'react-jwt';

import type {
    AccessAndIDTokenWithExpiresAt as AccessToken,
    AccessAndIDTokenResponse as AccessTokenResponse,
    BentleyAccessTokenDecoded,
} from '../../../apiClients/types';
import { bentleyIssuers, COMBINED_TOKEN_KEY, seequentIssuers } from '../../../constants';
import { getCurrentEvoInstance } from '../entitlementsHelper/entitlementsHelper';

export const AUTH_CODE_KEY = 'authCode';
export const DEFAULT_EXPIRY = 24 * 60 * 60 - 1; // 24 hours in seconds

export function setCombinedToken(accessTokenResponse: AccessTokenResponse): void {
    const accessToken: AccessToken = {
        ...accessTokenResponse,
        expires_at: Date.now() + (accessTokenResponse.expires_in || DEFAULT_EXPIRY) * 1000,
    };

    localStorage.setItem(COMBINED_TOKEN_KEY, JSON.stringify(accessToken));
}

export function getCombinedToken(): AccessToken | null {
    try {
        return JSON.parse(localStorage.getItem(COMBINED_TOKEN_KEY) || '{}') as AccessToken;
    } catch {
        return null;
    }
}

export function isTokenExpired(): boolean {
    const token = getCombinedToken();
    if (!token?.access_token) {
        return false;
    }
    return isExpired(token.access_token);
}

// true if Bentley token
// false if Seequent token
// undefined if no access token or could not be parsed
export function isLoggedInWithBentleyToken(): boolean | undefined {
    const combinedToken = getCombinedToken();
    const idTokenDecoded = decodeToken<{ iss: string }>(combinedToken?.id_token ?? '');
    const issuer = idTokenDecoded?.iss;

    if (issuer === undefined) {
        return undefined;
    }
    try {
        const issuerHostname = new URL(issuer).hostname;
        if (seequentIssuers.includes(issuerHostname)) {
            return false;
        }
        if (bentleyIssuers.includes(issuerHostname)) {
            return true;
        }
    } catch {
        return undefined;
    }
    return undefined;
}

export function getDecodedBentleyAccessToken(): BentleyAccessTokenDecoded | null {
    const combinedToken = getCombinedToken();
    if (combinedToken?.access_token) {
        return decodeToken<BentleyAccessTokenDecoded>(combinedToken.access_token);
    }
    return null;
}

export function clearCombinedToken(): void {
    localStorage.removeItem(COMBINED_TOKEN_KEY);
    sessionStorage.removeItem(AUTH_CODE_KEY);
}

export function getUserContext() {
    const accessToken = getCombinedToken()?.access_token;
    const decodedToken = accessToken ? decodeToken<BentleyAccessTokenDecoded>(accessToken) : null;
    const { org, hub } = getCurrentEvoInstance();
    const attributes = {};

    if (decodedToken !== null) {
        const { role } = decodedToken;
        const distinctRole = new Set(role);
        const loggedRole = typeof role === 'string' ? role : Array.from(distinctRole).join(' ');

        Object.assign(attributes, {
            [ATTRIBUTE_USER_ID]: decodedToken.sub,
            [ATTRIBUTE_USER_ULT_SITE]: decodedToken.ultimate_site,
            [ATTRIBUTE_USER_ORG_ID]: decodedToken.org,
            [ATTRIBUTE_CLIENT_ID]: decodedToken.client_id,
            [ATTRIBUTE_CLIENT_SCOPES]: decodedToken.scope.join(' '),
            [ATTRIBUTE_USER_ORG_ROLE]: loggedRole,
            [ATTRIBUTE_USER_COUNTRY]: decodedToken.usage_country_iso,
        });
    }

    if (hub?.code) {
        Object.assign(attributes, {
            [ATTRIBUTE_DEPLOY_HUB_CODE]: hub.code,
            [ATTRIBUTE_DEPLOY_HUB_ID]: hub.display_name,
        });
    }

    if (org?.id) {
        Object.assign(attributes, {
            [ATTRIBUTE_USER_ORG_ID]: org.id,
        });
    }

    return attributes;
}
